/* eslint-disable no-console */
import React, { useCallback, useState } from "react";
import { DataGridPro, GridColumns, GridRenderCellParams, GridToolbar, useGridApiRef } from "@mui/x-data-grid-pro";
import { Link } from "@material-ui/core";
import { inventoryDataGridProps } from "./InventoryDataGrid.d";
import appStyles from "../../AppStyles";
import { useGetInventory, useBatchUpdateInventory } from "../../api/replenishmentApi";
import { Button } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const InventoryDataGrid: React.FC<inventoryDataGridProps> = (props: inventoryDataGridProps) => {
	const { inventorySearchValues } = props;

	const apiRef = useGridApiRef();
	const classes = appStyles();
	const [{ data: inventory, loading }] = useGetInventory(
		inventorySearchValues.criteriaId,
		inventorySearchValues.locationIds
	);
	const [{ loading: isUpdating }, updateInventory] = useBatchUpdateInventory();
	const [editedRows, setEditedRows] = useState<Set<string>>(new Set());
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" } | null>(
		null
	);

	const handleCellEditCommit = useCallback((params: any) => {
		const { id, field } = params;
		if (field === "minQty" || field === "maxQty") {
			setEditedRows((prev) => new Set([...prev, id]));
		}
	}, []);

	const handleSaveChanges = useCallback(async () => {
		const rowsToUpdate = Array.from(editedRows).map((rowId) => {
			const row = apiRef.current.getRow(rowId);
			return {
				sku: row.sku,
				locationId: row.locationId,
				minQty: row.minQty,
				maxQty: row.maxQty,
			};
		});

		try {
			const response = await updateInventory(rowsToUpdate);
			// The BFF now returns a per-batch outcome (BHS-544). With small
			// interactive edits this is almost always a single batch, but we still
			// need to detect a rejected batch so we don't falsely report success.
			const failed = response.data?.failedBatches ?? [];
			if (failed.length === 0) {
				setEditedRows(new Set());
				setSnackbar({
					open: true,
					message: "Inventory updates saved successfully!",
					severity: "success",
				});
			} else {
				const successful = response.data?.successfulBatches ?? [];
				const failedList = failed.join(", ");
				const successList = successful.join(", ");
				setSnackbar({
					open: true,
					message:
						successful.length === 0
							? `Save failed. All ${failed.length} batch(es) were rejected (batches ${failedList}). Please retry.`
							: `Saved batches ${successList}; failed batches ${failedList}. Please retry.`,
					severity: "error",
				});
			}
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error saving inventory updates. Please try again.",
				severity: "error",
			});
		}
	}, [editedRows, apiRef, updateInventory]);

	const columns: GridColumns = [
		{
			field: "productImageUrl",
			headerName: "",
			width: 100,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<img
						src={
							params.value?.replace(".jpg", "_200x200.jpg")?.replace(".png", "_200x200.png") ?? "/images/noimage.png"
						}
						alt="product"
						width="64"
						height="64"
					/>
				);
			},
		},
		{
			field: "sku",
			headerName: "Sku",
			width: 100,
			disableColumnMenu: true,
			resizable: false,
			renderCell: (params) => {
				return (
					<Link
						href={`${process.env.REACT_APP_PRODUCT_URL}/product/${params.value}`}
						className={classes.link}
						target="_blank"
					>
						{params.value}
					</Link>
				);
			},
		},
		{ field: "description", headerName: "Description", width: 240, disableColumnMenu: true, resizable: false },
		{ field: "locationId", type: "number", headerName: "Location Id", width: 100 },
		{ field: "locationName", headerName: "Location", width: 150 },
		{ field: "totalSoh", type: "number", headerName: "SOH", width: 80 },
		{ field: "saleableSoh", type: "number", headerName: "Avail", width: 80 },
		{ field: "minQty", type: "number", headerName: "Min", width: 80, editable: true },
		{ field: "maxQty", type: "number", headerName: "Max", width: 80, editable: true },
		{ field: "isRanged", type: "boolean", headerName: "Ranged", width: 80 },
		{ field: "onPurchaseOrderQuantity", type: "number", headerName: "PO", width: 80 },
		{ field: "inTransitQuantity", type: "number", headerName: "Transit", width: 80 },
		{ field: "pendingCustomerOrderQuantity", type: "number", headerName: "CO", width: 80 },
		{ field: "inStoreOrderQuantity", type: "number", headerName: "Instore Co", width: 80 },
		{ field: "faultyPosReturnQuantity", type: "number", headerName: "Faulty", width: 80 },
		{ field: "displayQuantity", type: "number", headerName: "Display", width: 80 },
	];

	const CustomToolbar = () => {
		return (
			<div style={{ display: "flex", alignItems: "center", padding: 8 }}>
				<GridToolbar />
				{editedRows.size > 0 && (
					<Button color="primary" onClick={handleSaveChanges} disabled={isUpdating} style={{ marginLeft: 16 }}>
						{isUpdating ? "Saving..." : `Save Changes (${editedRows.size})`}
					</Button>
				)}
			</div>
		);
	};

	return (
		<>
			<DataGridPro
				apiRef={apiRef}
				autoHeight
				density="standard"
				loading={loading}
				rows={inventory ?? []}
				columns={columns}
				paginationMode="client"
				rowsPerPageOptions={[25, 50, 100]}
				pagination
				onCellEditCommit={handleCellEditCommit}
				onProcessRowUpdateError={(error) => console.error(error)}
				getRowId={(row) => `${row.sku}-${row.locationId}`}
				components={{
					Toolbar: CustomToolbar,
				}}
				initialState={{
					pagination: {
						pageSize: 25,
					},
					sorting: {
						sortModel: [{ field: "sku", sort: "asc" }],
					},
				}}
			/>
			<Snackbar
				open={!!snackbar?.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
					severity={snackbar?.severity}
					sx={{
						width: "100%",
						bgcolor: snackbar?.severity === "success" ? "success.main" : "error.main",
						color: "#fff",
					}}
				>
					{snackbar?.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default InventoryDataGrid;
